import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../hooks/useNotifications';
import { getNotificationDisplay } from './notificationContent';
import { ROUTES } from '../../constants/routes';

interface NotificationDropdownProps {
  className?: string;
  iconClassName?: string;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  className = '',
  iconClassName = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    isLoading,
    fetchList,
    markRead,
    markAllRead,
  } = useNotifications();

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      fetchList();
    }
  };

  const handleNotificationClick = async (id: number, to: string | null) => {
    await markRead(id);
    if (to) {
      navigate(to);
    }
    setIsOpen(false);
  };

  const handleViewAll = () => {
    navigate(ROUTES.NOTIFICATIONS);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          aria-label='การแจ้งเตือน'
          className={cn(
            'h-9 w-9 transition-all duration-200 relative group',
            className,
          )}
        >
          <Bell
            className={cn(
              'h-4 w-4 group-hover:scale-110 transition-transform duration-200',
              iconClassName,
            )}
          />
          {unreadCount > 0 && (
            <span className='absolute -right-1 -top-1 min-w-5 h-5 px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold leading-5 text-center shadow-sm'>
              {unreadCount > 9 ? '9+' : String(unreadCount)}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent className='w-80 p-0' align='end' sideOffset={8}>
        {/* Header */}
        <div className='px-4 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3'>
          <h3 className='text-sm font-semibold text-gray-900'>การแจ้งเตือน</h3>
          {unreadCount > 0 && (
            <button
              type='button'
              onClick={markAllRead}
              className='text-xs font-medium text-blue-600 hover:text-blue-700'
            >
              อ่านทั้งหมด
            </button>
          )}
        </div>

        {notifications.length > 0 ? (
          <div className='max-h-96 overflow-y-auto'>
            {notifications.map((notification) => {
              const display = getNotificationDisplay(notification.content);
              return (
                <button
                  key={notification.id}
                  type='button'
                  onClick={() =>
                    handleNotificationClick(notification.id, display.to)
                  }
                  className={cn(
                    'w-full text-left px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors',
                    !notification.isRead && 'bg-blue-50/50',
                  )}
                >
                  <div className='flex gap-3'>
                    <span
                      className={cn(
                        'mt-1.5 h-2 w-2 rounded-full flex-shrink-0',
                        notification.isRead ? 'bg-transparent' : 'bg-blue-500',
                      )}
                    />
                    <div className='min-w-0 flex-1'>
                      <div className='text-sm font-medium text-gray-900'>
                        {display.title}
                      </div>
                      {display.description && (
                        <div className='mt-0.5 text-xs text-gray-500'>
                          {display.description}
                        </div>
                      )}
                      {display.preview && (
                        <div className='mt-1 line-clamp-2 rounded-lg bg-white/70 px-2 py-1 text-xs text-gray-600'>
                          “{display.preview}”
                        </div>
                      )}
                      {display.details.length > 0 && (
                        <div className='mt-1 flex flex-wrap gap-1'>
                          {display.details.map((detail) => (
                            <span
                              key={detail}
                              className='rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-600'
                            >
                              {detail}
                            </span>
                          ))}
                        </div>
                      )}
                      <div className='mt-1 text-[11px] text-gray-400'>
                        {formatRelativeTimeTh(notification.createdAt)}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className='px-4 py-8 text-center'>
            <div className='w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center'>
              <Bell className='h-6 w-6 text-gray-400' />
            </div>
            <h4 className='text-sm font-medium text-gray-900 mb-1'>
              {isLoading ? 'กำลังโหลด...' : 'ไม่มีการแจ้งเตือน'}
            </h4>
          </div>
        )}

        <div className='border-t border-gray-100 bg-white px-3 py-2'>
          <button
            type='button'
            onClick={handleViewAll}
            className='w-full rounded-lg px-3 py-2 text-center text-xs font-semibold text-blue-600 hover:bg-blue-50'
          >
            ดูการแจ้งเตือนทั้งหมด
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const formatRelativeTimeTh = (iso: string) => {
  const createdAt = new Date(iso).getTime();
  if (Number.isNaN(createdAt)) {
    return '';
  }

  const diffMs = Date.now() - createdAt;
  const diffMinutes = Math.max(0, Math.floor(diffMs / 60_000));

  if (diffMinutes < 1) {
    return 'เมื่อสักครู่';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} นาทีที่แล้ว`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} ชม.ที่แล้ว`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} วันที่แล้ว`;
};

export default NotificationDropdown;
